import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { RootState } from "store/store";
import { useSelector } from "react-redux";
import ArthaGateway from "service/ArthaGateway";
import {
  Party,
  SaleOrder,
  Product,
  OrderItem,
  OrderDocument,
  ProductMaster,
  ProductStyle,
} from "service";
import defaultImage from "../../assets/default.png";
import {
  faChevronLeft,
  faFile,
  faGripVertical,
  faTableCells,
  faTableList,
  faTrash,
  faPlus,
  faMinus,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import toast, { Toaster } from "react-hot-toast";
import { FileUpload, Loader } from "../Widgets";
import swal from 'sweetalert';

// NOTE: This file is a modular, backwards-compatible rewrite of your original NewOrder.tsx
// - UI improved (cleaner cards, clear selection states)
// - Supports multi-style selection with per-style quantities
// - Tick marks on selected styles, click-anywhere-to-select/unselect
// - When adding to order we still push separate OrderItem entries (final order structure unchanged)

// -----------------------
// Helper subcomponents
// -----------------------

const Badge = ({ children }: { children: React.ReactNode }) => (
  <span className="badge bg-secondary">{children}</span>
);

const SmallButton = ({ onClick, children }: { onClick: () => void; children: React.ReactNode }) => (
  <button className="btn btn-sm btn-outline-secondary" onClick={onClick}>
    {children}
  </button>
);

const ImageOrDefault = ({ src, alt, style }: { src?: string | null; alt?: string; style?: React.CSSProperties }) => (
  <img
    src={src && src.length > 0 ? src : defaultImage}
    alt={alt || "image"}
    className="img-fluid"
    style={{ maxHeight: 250, objectFit: "cover", width: "100%", ...style }}
  />
);

// -----------------------
// Types for local UI state
// -----------------------

type SelectedStyle = {
  style: ProductStyle;
  quantity: number;
};

// -----------------------
// Main component
// -----------------------

export const NewOrder = () => {
  const user = useSelector((state: RootState) => state.appdata);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isProductsLoading, setIsProductsLoading] = useState<boolean>(false);
  const [screenMode, setScreenMode] = useState<string>("main-screen");
  const [party, setParty] = useState<Party | undefined>();
  const [productGroups, setProductGroups] = useState<any[]>([]);
  const [productBrands, setProductBrands] = useState<any[]>([]);
  const [products, setProducts] = useState<ProductMaster[]>([]);
  const [productStyles, setProductStyles] = useState<ProductStyle[]>([]);
  const [styleGroups, setStyleGroups] = useState<Record<string, ProductStyle[]>>({});
  const [productGroup, setProductGroup] = useState<any>();
  const [productBrand, setProductBrand] = useState<any>();
  const [productName, setproductName] = useState<string>("");
  const [styleName, setStyleName] = useState<string>("");
  const [selectionMode, setSelectionMode] = useState<string>("grid");
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(25);
  const [totalPages, setTotalPages] = useState<number>(5);
  const [orderItem, setOrderItem] = useState<OrderItem | null>(null);
  const [selectedStyles, setSelectedStyles] = useState<SelectedStyle[]>([]);

  const [order, setOrder] = useState<SaleOrder>(new SaleOrder());
  const [isOrderSaving, setIsOrderSaving] = useState<boolean>(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (user.profile) {
      setParty(user.profile.profile);
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (party) {
      order.shippingStateCode = party.billingStateCode;
      order.shippingAddress1 = party.billingAddress1;
      order.shippingAddress2 = party.billingAddress2;
      order.shippingCity = party.billingCity;
      order.shippingPinCode = party.billingPinCode;

      setOrder({ ...order, party: party });
      loadProductGroups();
      loadProductBrands();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [party]);

  useEffect(() => {
    loadProductMasters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber]);

  useEffect(() => {
    loadPartyDetails();
    let value = localStorage.getItem("selectionMode");
    if (value) setSelectionMode(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Group styles when productStyles or search text changes
    const groups = groupStyles(productStyles.filter((s) => (s.name || "").toLowerCase().includes(styleName.toLowerCase())));
    setStyleGroups(groups);
  }, [productStyles, styleName]);

  // -----------------------
  // Data loaders
  // -----------------------
  const loadProductMasters = () => {
    setIsProductsLoading(true);
    ArthaGateway.getProductMasters(0, 0, productName, true)
      .then((data) => {
        setProducts(data.entries.filter((p: ProductMaster) => p.enableOnlineSales === true));
        if (screenMode === "main-screen" && order.items.length === 0) {
          setScreenMode("select-product");
        }
        setTotalPages(data.totalPages);
        sessionStorage.setItem("productMasters", JSON.stringify(data.entries.filter((p: ProductMaster) => p.enableOnlineSales === true)));
        setIsProductsLoading(false);
      })
      .catch((error) => {
        console.error(error);
        setIsProductsLoading(false);
        setIsLoading(false);
      });
  };

  const loadProductBrands = () => {
    if (productBrands.length > 0) return;
    ArthaGateway.getProductBrands()
      .then((data) => {
        data.forEach((element: any) => (element.products = []));
        sessionStorage.setItem("brands", JSON.stringify(data));
        setProductBrands(data);
        setProductBrand(data[0]);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const loadProductGroups = () => {
    if (productGroups.length > 0) return;
    if (sessionStorage.getItem("catagories") === null) {
      ArthaGateway.getProductGroups()
        .then((data) => {
          data = data.filter((g: any) => g.id !== 999);
          data.forEach((element: any) => (element.products = []));
          sessionStorage.setItem("catagories", JSON.stringify(data));
          setProductGroups(data);
          setProductGroup(data[0]);
        })
        .catch((error) => {
          console.log(error);
          setIsLoading(false);
        });
    } else {
      const localData = sessionStorage.getItem("catagories");
      if (localData) {
        const catagories = JSON.parse(localData);
        setProductGroups(catagories);
        setProductGroup(catagories[0]);
      }
    }
  };

  const loadPartyDetails = () => {
    // placeholder for future extension
  };

  const loadProductStyles = (productMasterId?: number) => {
    if (!productMasterId) return;
    ArthaGateway.getProductStyles(productMasterId)
      .then((data) => {
        setProductStyles(data || []);
      })
      .catch((error) => {
        console.error(error);
        setIsLoading(false);
      });
  };

  // -----------------------
  // Selection handlers
  // -----------------------

  const selectProductMaster = (product: ProductMaster) => {
    localStorage.setItem("selectionMode", selectionMode);
    ArthaGateway.getProductMaster(product.id)
      .then((data) => {
        if (data) {
          const item: OrderItem = new OrderItem();
          item.productMaster = data;
          item.quantity = 1;
          item.product = data.products[0];
          item.options = structuredClone(data.products[0].options);
          setOrderItem(item);
          setSelectedStyles([]); // reset any previous style selections
          setScreenMode("select-size");
          loadProductStyles(data.id);
        }
      })
      .catch((error) => console.error(error));
  };

  const handleOptionChange = (value, optionName) => {
    if (!orderItem) {
      return;
    }
    let currentOption = orderItem?.options.find(option => option.name === optionName);
    if (currentOption) {
      currentOption.value = value;
      setOrderItem({ ...orderItem, options: [...orderItem.options] });
    }
  };

  // Toggle style select/unselect
  const isStyleSelected = (s: ProductStyle) => selectedStyles.some((x) => x.style.id === s.id);

  const toggleStyle = (s: ProductStyle) => {
    if (!orderItem) return;
    const exists = selectedStyles.find((x) => x.style.id === s.id);
    if (exists) {
      // unselect
      setSelectedStyles(selectedStyles.filter((x) => x.style.id !== s.id));
    } else {
      // select with default quantity 1
      setSelectedStyles([...selectedStyles, { style: s, quantity: 1 }]);
    }
  };

  const updateStyleQty = (styleId: number, quantity: number) => {
    if (quantity < 1) quantity = 1;
    setSelectedStyles((prev) => prev.map((s) => (s.style.id === styleId ? { ...s, quantity } : s)));
  };

  const handleAddAnotherStyle = () => {
    // reset style selection while keeping product context
    if (!orderItem) return;
    setOrderItem({ ...orderItem, quantity: 1 } as OrderItem);
    setSelectedStyles([]);
    setScreenMode("select-size");
  };

  const handleAddAnotherSize = () => {
    if (!orderItem) return;
    setOrderItem({ ...orderItem, quantity: 1 } as OrderItem);
    setScreenMode("select-size");
  };

  const incrementQuantity = (item: OrderItem) => {
    if (!item) return;
    item.quantity++;
    setOrder({ ...order });
  };

  const decrementQuantity = (item: OrderItem) => {
    if (!item) return;
    if (item.quantity > 1) item.quantity--;
    setOrder({ ...order });
  };

  // Selecting a single style (legacy) kept for fallback
  const selectProductMasterStyle = (style: ProductStyle) => {
    if (!orderItem) return;
    // replace selectedStyles with single selection to keep UX predictable
    setSelectedStyles([{ style, quantity: orderItem.quantity || 1 }]);
    setScreenMode("select-size");
  };

  // -----------------------
  // Add to order (important: preserve final order structure)
  // We create separate OrderItem entries for each selected style
  // -----------------------
  const addOrderItem = (item: OrderItem | null) => {
    if (!item) return;

    // Ensure options are mapped to a real product object
    selectProductBasedOnOptions();

    // ensure we have selected styles OR fallback to a single style (existing behavior)
    const stylesToAdd = selectedStyles.length > 0 ? selectedStyles : item.style ? [{ style: item.style, quantity: item.quantity }] : [];

    if (stylesToAdd.length === 0) {
      // No style selected; if customImage is present treat as single item      
      if (!item.customImageUrl && !item.style) {
        // Prompt user to confirm adding without style
        swal({
            title: "No style selected",
            text: "No style selected. Do you want to proceed without selecting a style?",
            icon: "warning",
            dangerMode: true,
            buttons: {
                default: { text: 'Cancel' },
                cancel: { text: 'No, Select Style' },
                confirm: { text: 'Yes, Add without style' }
            }
          })
          .then((confirmAdd) => {
            if (confirmAdd === true) {
                updatePricingDetails(item);
                console.log(item);
                setCalculatedValues(item, order);
                order.items.push(item);

                 // recalc order and update
                calculateOrderTotal(order);
                setOrder({ ...order, items: [...order.items] });

                // move to confirmation 'item added' view
                setScreenMode("item-added");
            }
          });
          return;
      }
      else if (item.customImageUrl) {
        // If custom styleis selected we add as single item
        updatePricingDetails(item);
        setCalculatedValues(item, order);
        order.items.push(item);
      }
    }
    else {
      // For each selected style we clone the item and push into order.items
      stylesToAdd.forEach((sel) => {
        const newItem: OrderItem = new OrderItem();
        // shallow copy the existing orderItem (but not the styles array)
        Object.assign(newItem, { ...(item as any) });

        // set style and quantity specific to this selection
        newItem.style = sel.style;
        newItem.quantity = sel.quantity;

        // Update product-based values for calculations
        updatePricingDetails(newItem);

        // calculate values for this item
        setCalculatedValues(newItem, order);

        order.items.push(newItem);
      });
    }

    // recalc order and update
    calculateOrderTotal(order);
    setOrder({ ...order, items: [...order.items] });

    // move to confirmation 'item added' view
    setScreenMode("item-added");
  };

  const updatePricingDetails = (item: OrderItem) => {
    item.price = item.product.price;
    item.unitId = item.product.unitOfMeasureId;
    item.unit = item.product.unitOfMeasureCode;
    item.priceIncludesTax = item.product.priceIncludesTax;
    item.discountPercentage = item.product.discount;
    item.isDiscountInPercentage = item.product.isDiscountInPercentage;
    item.squareFeet = item.product.squareFeet;
    item.cgstTaxPercentage = item.product.cgstTax.percentage;
    item.sgstTaxPercentage = item.product.sgstTax.percentage;
    item.igstTaxPercentage = item.product.igstTax.percentage;
  }

  const selectProductBasedOnOptions = () => {
    if (!orderItem || !orderItem.productMaster || !orderItem.productMaster.products || !orderItem.options) {
      console.warn('Missing productMaster.products or currentItem.options');
      return;
    }

    const selectedOptions = orderItem.options;
    const productsList = orderItem.productMaster.products;

    const matchingProduct = productsList.find((product) => {
      if (!product.options || product.options.length === 0) return false;

      return Object.entries(selectedOptions).every(([optionName, selectedValue]) => {
        const productOption = product.options.find((opt: any) => opt.name === selectedValue.name);
        if (!productOption) return false;
        if (productOption.value == null && selectedValue.value == null) return true;
        if (productOption.value == null || selectedValue.value == null) return false;
        const productStr = productOption.value?.toString().trim().toLowerCase();
        const selectedStr = selectedValue.value?.toString().trim().toLowerCase();
        return productStr === selectedStr;
      });
    });

    if (matchingProduct) {
      if (orderItem) orderItem.product = matchingProduct;
    } else {
      if (orderItem) orderItem.product = productsList[0];
    }
  };

  const setCalculatedValues = (item: OrderItem, orderObj: SaleOrder) => {
    const grossValue = item.product.price * item.quantity;
    item.discount = 0;
    if (item.product.discount > 0) item.discount = grossValue * (item.product.discount / 100);
    item.taxableValue = grossValue - item.discount;
    // NOTE: original code had a parenthesis bug when adding cgst+sgst percentages. Keep same logic but corrected.
    const taxPercentage = (item.product?.cgstTax?.percentage || 0) + (item.product?.sgstTax?.percentage || 0);
    const tax = (item.taxableValue * taxPercentage) / 100;
    item.cgstTax = tax / 2;
    item.sgstTax = tax / 2;
    item.igstTax = 0;
    item.netValue = item.taxableValue + item.cgstTax + item.sgstTax + item.igstTax;
  };

  const calculateOrderTotal = (orderObj: SaleOrder) => {
    orderObj.taxableValue = orderObj.items.reduce((a: number, b: any) => a + (b.taxableValue || 0), 0);
    orderObj.discount = orderObj.items.reduce((a: number, b: any) => a + (b.discount || 0), 0);
    orderObj.cgstTax = orderObj.items.reduce((a: number, b: any) => a + (b.cgstTax || 0), 0);
    orderObj.sgstTax = orderObj.items.reduce((a: number, b: any) => a + (b.sgstTax || 0), 0);
    orderObj.igstTax = orderObj.items.reduce((a: number, b: any) => a + (b.igstTax || 0), 0);
    orderObj.netValue = orderObj.items.reduce((a: number, b: any) => a + (b.netValue || 0), 0);
    orderObj.roundOff = orderObj.netValue - Math.round(orderObj.netValue);
    orderObj.billAmount = Math.round(orderObj.netValue);
  };

  const updateQuantity = (e: any, index: number) => {
    let quantity = e.target.value;
    if (e.target.value.length === 0) quantity = 0;
    const item = order.items[index];
    item.quantity = parseInt(quantity);
    setCalculatedValues(item, order);
    calculateOrderTotal(order);
    setOrder({ ...order, items: [...order.items] });
  };

  const updateOrderNotes = (e: any) => setOrder({ ...order, notes: e.target.value });

  const saveOrder = () => {
    if (order.items.length === 0) {
      toast('Please add products to the order', { position: 'bottom-center', className: 'btn bg-danger' });
      return;
    }
    if (isOrderSaving) {
      toast('Order is already being saved', { position: 'bottom-center', className: 'btn bg-warning' });
      return;
    }

    setIsOrderSaving(true);
    order.date = moment().format("YYYY-MM-DD");
    order.dueDate = moment().add(15, "days").format("YYYY-MM-DD");
    order.series = "HEAD OFFICE";
    order.branch = { id: 1, name: "HEAD OFFICE", code: "HEAD OFFICE" };
    order.status = 1;

    ArthaGateway.saveOrder(order)
      .then((data) => {
        toast('Order saved successfully', { position: 'bottom-center', className: 'btn bg-success' });
        setIsOrderSaving(false);
        setTimeout(() => navigate("/order"), 2000);
      })
      .catch((err) => {
        console.error(err);
        setIsOrderSaving(false);
      });
  };

  const showConfirmOrder = () => {
    if (order.items.length === 0) {
      toast('Please add products to the order', { position: 'bottom-center', className: 'btn bg-danger' });
      return;
    }
    setScreenMode("confirm-order");
  };

  const removeCartItem = (index: number) => {
    const items = [...order.items];
    items.splice(index, 1);
    order.items = items;
    calculateOrderTotal(order);
    setOrder({ ...order, items });
  };

  const getDisplayImage = (item: any) => {
    if (item.customImageUrl && item.customImageUrl.length > 0) return item.customImageUrl;
    if (item.style?.imageUrl) return item.style.imageUrl;
    if (item.productMaster?.imageUrl) return item.productMaster.imageUrl;
    return null;
  };

  const groupStyles = (styles: ProductStyle[]) => {
    return styles.reduce((groups: Record<string, ProductStyle[]>, style) => {
      const key = style.groupName && style.groupName.trim().length > 0 ? style.groupName : "Other Styles";
      if (!groups[key]) groups[key] = [];
      groups[key].push(style);
      return groups;
    }, {} as Record<string, ProductStyle[]>);
  };

  const currencyFormat = (value: number) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(value);

  // -----------------------
  // Rendering blocks
  // -----------------------
  return (
    <div className="page page-new-order container py-3">
      {isLoading && <Loader />}
      {!isLoading && (
        <>
          <div>
            <Toaster />
          </div>

          {screenMode === "main-screen" && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="m-0">New Order</h2>
                <div>
                  <button className="btn btn-dark btn-sm" onClick={() => setScreenMode("select-product")}>
                    <FontAwesomeIcon icon={faPlus} className="me-2" /> Select Product
                  </button>
                </div>
              </div>

              {order.party && (
                <>
                  <div className="row">
                    {order.items.length === 0 && (
                      <div className="col-12">
                        <div className="alert alert-warning">Your cart is empty — add products to start.</div>
                      </div>
                    )}

                    {order.items.map((item, index) => (
                      <div className="col-12 col-md-6 col-lg-4 mb-3" key={index}>
                        <div className="card h-100 shadow-sm">
                          <div className="card-img-top text-center p-2">
                            {getDisplayImage(item) ? (
                              <ImageOrDefault src={getDisplayImage(item)} alt={item.product?.name} />
                            ) : (
                              <ImageOrDefault src={null} alt={item.product?.name} />
                            )}
                          </div>
                          <div className="card-body d-flex flex-column">
                            <h5 className="card-title">{item.product.name}</h5>
                            {item.style && (
                              <div className="mb-1">Style: <strong>{item.style.name}</strong></div>
                            )}
                            {item.notes && <div className="small text-muted">{item.notes}</div>}

                            <div className="mt-auto d-flex justify-content-between align-items-center pt-3">
                              <div>
                                <button className="btn btn-danger btn-sm me-2" onClick={() => removeCartItem(index)}>
                                  <FontAwesomeIcon icon={faTrash} /> Remove
                                </button>
                              </div>

                              <div className="d-flex align-items-center">
                                <SmallButton onClick={() => decrementQuantity(item)}>
                                  <FontAwesomeIcon icon={faMinus} />
                                </SmallButton>
                                <div className="px-2 fw-bold">{item.quantity}</div>
                                <SmallButton onClick={() => incrementQuantity(item)}>
                                  <FontAwesomeIcon icon={faPlus} />
                                </SmallButton>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="row mt-3">
                    <div className="col-md-8">
                      <textarea
                        className="form-control"
                        rows={3}
                        placeholder="Order notes (optional)"
                        value={order.notes}
                        onChange={updateOrderNotes}
                      />
                    </div>
                    <div className="col-md-4 d-flex flex-column gap-2">
                      <button className="btn btn-success" onClick={() => showConfirmOrder()} disabled={order.items.length === 0}>
                        Create Order
                      </button>
                      <button className="btn btn-outline-secondary" onClick={() => setScreenMode("select-product")}>
                        Add Another Product
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {screenMode === "select-product" && (
            <>
              <div className="mb-3 d-flex align-items-center gap-2">
                <input className="form-control" value={productName} onChange={(e) => setproductName(e.target.value)} placeholder="Search by name" />
                <div className="btn-group" role="group">
                  <button className={`btn btn-sm ${selectionMode === "grid" ? "btn-dark" : "btn-outline-secondary"}`} onClick={() => setSelectionMode("grid")}>
                    <FontAwesomeIcon icon={faGripVertical} />
                  </button>
                  <button className={`btn btn-sm ${selectionMode === "table" ? "btn-dark" : "btn-outline-secondary"}`} onClick={() => setSelectionMode("table")}>
                    <FontAwesomeIcon icon={faTableList} />
                  </button>
                </div>
              </div>

              <div className="accordion" id="brandsAccordion">
                {productBrands.map((brand: any, i: number) => (
                  <div className="accordion-item" key={brand.id}>
                    <h2 className="accordion-header" id={`brandHeading${i}`}>
                      <button className={`accordion-button ${i !== 0 ? "collapsed" : ""}`} type="button" data-bs-toggle="collapse" data-bs-target={`#brandCollapse${i}`} aria-expanded={i === 0 ? "true" : "false"} aria-controls={`brandCollapse${i}`}>
                        <strong>{brand.name}</strong>
                        <small className="ms-2 text-muted">({products.filter((p) => p.brandId === brand.id).length})</small>
                      </button>
                    </h2>
                    <div id={`brandCollapse${i}`} className={`accordion-collapse collapse ${i === 0 ? "show" : ""}`} aria-labelledby={`brandHeading${i}`} data-bs-parent="#brandsAccordion">
                      <div className="accordion-body">
                        {selectionMode === "table" ? (
                          <table className="table table-sm">
                            <tbody>
                              {products.filter((product) => product.brandId === brand.id).map((product) => (
                                <tr key={product.id}>
                                  <td>{product.name}</td>
                                  <td style={{ width: 160 }}>
                                    <button className="btn btn-dark btn-sm w-100" onClick={() => selectProductMaster(product)}>
                                      Select
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <div className="row">
                            {products.filter((product) => product.brandId === brand.id).map((product) => (
                              <div className="col-6 col-md-4 col-lg-3 mb-3" key={product.id}>
                                <div className="card h-100 shadow-sm">
                                  <div className="card-img-top text-center">
                                    <ImageOrDefault src={product.imageUrl} alt={product.name} />
                                  </div>
                                  <div className="card-body d-flex flex-column p-2">
                                    <div className="card-title mb-2">{product.name}</div>
                                    <button className="btn btn-dark btn-sm mt-auto" onClick={() => selectProductMaster(product)}>Select</button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {(totalPages > pageNumber) && (
                          <div className="text-center mt-3">
                            {isProductsLoading ? (
                              <div className="spinner-border" role="status"></div>
                            ) : (
                              <button className="btn btn-dark" onClick={() => setPageNumber(pageNumber + 1)}>Load More</button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-3">
                <button className="btn btn-danger" onClick={() => setScreenMode("main-screen")}>
                  <FontAwesomeIcon icon={faChevronLeft} /> Back
                </button>
              </div>
            </>
          )}

          {screenMode === "select-size" && orderItem && (
            <>
              <div className="text-center mb-3">
                <h3 className="m-0">{orderItem.productMaster?.name}</h3>
                <small className="text-muted">Select size, styles and quantity</small>
              </div>

              {orderItem.productMaster.options && orderItem.productMaster.options.length > 0 && (
                <div className="row mb-2">
                  {orderItem.productMaster.options.map((option: any, idx: number) => (
                    <div className="col-6 col-md-4 mb-2" key={idx}>
                      <label className="form-label">{option.name}</label>
                      <select className="form-select" value={orderItem.options?.find((o: any) => o.name === option.name)?.value || ""} onChange={(e) => {
                        handleOptionChange(e.target.value, option.name);
                      }}>
                        {option.values.map((v: any, i: number) => (
                          <option key={i} value={v}>{v}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              )}

              {/* Selected single style preview (legacy) */}
              {selectedStyles.length === 1 && (
                <div className="text-center mb-3">
                  <ImageOrDefault src={selectedStyles[0].style.imageUrl} alt={selectedStyles[0].style.name} />
                  <div className="mt-2"><strong>{selectedStyles[0].style.name}</strong></div>
                </div>
              )}

              {/* Button to open style picker */}
              <div className="mb-3">
                <button className="btn btn-dark w-100" onClick={() => setScreenMode("select-style")}>Select Styles</button>
              </div>

              {/* Custom upload */}
              {!orderItem.customImageUrl && selectedStyles.length === 0 && (
                <div className="mb-3">
                  <FileUpload
                    buttonName="Upload Custom Style"
                    folder="public/product/style/custom"
                    onFileUpload={(fileUrl: string) => setOrderItem(Object.assign(new OrderItem(), { ...orderItem, customImageUrl: fileUrl }))}
                  />
                </div>
              )}

              {/* Per-item quantity (applies when user wants same qty for every style or single style fallback) */}
              <div className="row align-items-center mb-3">
              {selectedStyles.length === 0 && (
                  <div className="col-12 col-md-4 mb-2">
                    <label className="form-label w-100 text-center">Quantity (default)</label>
                    <div className="d-flex justify-content-center align-items-center gap-2">
                      <SmallButton onClick={() => setOrderItem(Object.assign(new OrderItem(), { ...orderItem, quantity: Math.max(1, (orderItem?.quantity || 1) - 1) }))}>
                        <FontAwesomeIcon icon={faMinus} />
                      </SmallButton>
                      <input type="number" min={1} value={orderItem.quantity} onChange={(e) => setOrderItem(Object.assign(new OrderItem(), { ...orderItem, quantity: Number(e.target.value || 1) }))} className="form-control text-center" style={{ width: 120 }} />
                      <SmallButton onClick={() => setOrderItem(Object.assign(new OrderItem(), { ...orderItem, quantity: (orderItem?.quantity || 1) + 1 }))}>
                        <FontAwesomeIcon icon={faPlus} />
                      </SmallButton>
                    </div>
                  </div>
                )}

                <div className="col-12 col-md-8">
                  {/* Per-selected-style quantity editor */}
                  {selectedStyles.length > 0 && (
                    <div className="card p-2 shadow-sm">
                      <div className="mb-2"><strong>Selected Styles</strong></div>
                      {selectedStyles.map((s) => (
                        <div key={s.style.id} className="d-flex align-items-center justify-content-between mb-2">
                          <div className="d-flex align-items-center gap-2">
                            <img src={s.style.imageUrl || defaultImage} alt={s.style.name} style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4 }} />
                            <div>{s.style.name}</div>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <SmallButton onClick={() => updateStyleQty(s.style.id, Math.max(1, s.quantity - 1))}><FontAwesomeIcon icon={faMinus} /></SmallButton>
                            <input type="number" value={s.quantity} className="form-control text-center" style={{ width: 80 }} onChange={(e) => updateStyleQty(s.style.id, Number(e.target.value || 1))} />
                            <SmallButton onClick={() => updateStyleQty(s.style.id, s.quantity + 1)}><FontAwesomeIcon icon={faPlus} /></SmallButton>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

              </div>

              <div className="mb-3">
                <label className="form-label">Notes</label>
                <textarea className="form-control" rows={3} value={orderItem.notes || ""} onChange={(e) => setOrderItem(Object.assign(new OrderItem(), { ...orderItem, notes: e.target.value }))} />
              </div>

              <div className="d-grid gap-2">
                <button className="btn btn-dark btn-lg" onClick={() => addOrderItem(orderItem)}>Add to Order</button>
              </div>

              <div className="mt-3">
                <button className="btn btn-danger" onClick={() => setScreenMode("main-screen")}>
                  <FontAwesomeIcon icon={faChevronLeft} /> Back
                </button>
              </div>
            </>
          )}

          {screenMode === "item-added" && (
            <div className="after-add-box shadow rounded p-3 mx-2 my-3 bg-white">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="mb-0">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" /> Item(s) added to order successfully!
                </h5>
              </div>

              <div className="p-2 rounded bg-light mb-3 small">
                <div><strong>Product:</strong> {orderItem?.product?.name}</div>
                {selectedStyles.length > 0 ? (
                  <div>
                    <strong>Styles:</strong>
                    <ul>
                      {selectedStyles.map((s) => (
                        <li key={s.style.id}>{s.style.name} x {s.quantity}</li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  orderItem?.style && <div><strong>Style:</strong> {orderItem.style.name}</div>
                )}
                <div><strong>Quantity:</strong> {orderItem?.quantity}</div>
              </div>

              <div className="d-grid gap-2 mt-3">
                <button className="btn btn-primary py-2" onClick={handleAddAnotherStyle}>Add Another Style</button>
                <button className="btn btn-primary py-2" onClick={handleAddAnotherSize}>Add Another Size</button>
                <button className="btn btn-success py-2" onClick={() => setScreenMode("main-screen")}>Go to Cart</button>
                <button className="btn btn-secondary py-2" onClick={() => setScreenMode("select-product")}>Add another Product</button>
              </div>
            </div>
          )}

          {screenMode === "select-style" && (
            <>
              <div className="text-center mb-3">
                <h3 className="m-0">{orderItem?.productMaster?.name}</h3>
                <small className="text-muted">Click any card or image to select / unselect a style</small>
              </div>

              <div className="form-group mb-3">
                <input className="form-control" value={styleName} onChange={(e) => setStyleName(e.target.value)} placeholder="Filter styles" />
              </div>

              <div className="styles-holder">
                <div className="accordion" id="stylesAccordion">
                  {Object.keys(styleGroups).map((group, idx) => (
                    <div className="accordion-item" key={group}>
                      <h2 className="accordion-header" id={`heading-${idx}`}>
                        <button className={`accordion-button ${idx !== 0 ? "collapsed" : ""}`} type="button" data-bs-toggle="collapse" data-bs-target={`#collapse-${idx}`} aria-expanded={idx === 0 ? "true" : "false"} aria-controls={`collapse-${idx}`}>
                          {group} ({styleGroups[group].length})
                        </button>
                      </h2>
                      <div id={`collapse-${idx}`} className={`accordion-collapse collapse ${idx === 0 ? "show" : ""}`} aria-labelledby={`heading-${idx}`} data-bs-parent="#stylesAccordion">
                        <div className="accordion-body p-2">
                          <div className="row justify-content-center">
                            {styleGroups[group].map((style, index) => {
                              const selected = isStyleSelected(style);
                              return (
                                <div key={index} className="col-6 col-md-4 col-lg-3 mb-3">
                                  <div className={`card h-100 shadow-sm style-card ${selected ? 'border-success' : ''}`} onClick={() => toggleStyle(style)} style={{ cursor: 'pointer' }}>
                                    <div className="card-img-top position-relative">
                                      <ImageOrDefault src={style.imageUrl} alt={style.name} />
                                      {selected && (
                                        <div style={{ position: 'absolute', right: 8, top: 8 }}>
                                          <FontAwesomeIcon icon={faCheckCircle} className="text-success" size="lg" />
                                        </div>
                                      )}
                                    </div>
                                    <div className="card-body d-flex flex-column p-2 text-center">
                                      <div className="card-title mb-2 text-truncate">{style.name}</div>
                                      <div className="mt-auto">
                                        <button className={`btn btn-sm ${selected ? 'btn-outline-danger' : 'btn-dark'} w-100`} onClick={(e) => { e.stopPropagation(); toggleStyle(style); }}>
                                          {selected ? 'Unselect' : 'Select'}
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {selectedStyles.length > 0 && (
                <div
                  className="position-fixed bottom-0 start-0 end-0 p-3"
                  style={{ zIndex: 9999 }}
                >
                  <div className="card shadow-lg p-3 bg-dark text-white" style={{ borderRadius: 14 }}>
                    <div className="text-center mb-2">
                      <strong>{selectedStyles.length}</strong> style(s) selected
                    </div>

                    <button
                      className="btn btn-success w-100 mb-2"
                      onClick={() => setScreenMode("select-size")}
                    >
                      Done Selecting Styles
                    </button>

                    <button
                      className="btn btn-outline-light w-100"
                      onClick={() => setSelectedStyles([])}
                    >
                      Clear Selection
                    </button>
                  </div>
                </div>
              )}


              <div className="mt-3 d-flex gap-2">
                <button className="btn btn-dark" onClick={() => setScreenMode("select-size")}>Done</button>
                <button className="btn btn-secondary" onClick={() => { setSelectedStyles([]); setScreenMode("select-size"); }}>Clear Selection</button>
              </div>

              <div className="mt-3">
                <button className="btn btn-danger" onClick={() => setScreenMode("select-size")}>
                  <FontAwesomeIcon icon={faChevronLeft} /> Back
                </button>
              </div>
            </>
          )}

          {screenMode === "confirm-order" && (
            <div>
              <div className="card mb-2 bg-primary text-white">
                <div className="card-header">
                  <h2 className="m-0">{order.party?.name}</h2>
                </div>
              </div>

              <div className="row">
                <div className="col-12">
                  <table className="table table-sm">
                    <tbody>
                      {order.items.map((item, index) => (
                        <tr key={index}>
                          <td>
                            {item.product.name}
                            {item.style && (
                              <><br />Style: <b>{item.style.name}</b></>
                            )}
                            {item.notes && (<><br />Notes: <b>{item.notes}</b></>)}
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 'bolder', width: 120 }}>{item.quantity}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="row">
                <div className="col">
                  {isOrderSaving ? (
                    <div className='btn btn-secondary btn-lg w-100 mb-2 mt-2' title="Saving Order">
                      <div className="spinner-border text-light" role="status"></div>
                    </div>
                  ) : (
                    <div className="btn btn-success btn-lg w-100 mb-2 mt-2" onClick={() => saveOrder()}>Confirm Order</div>
                  )}
                </div>
              </div>

              <div className="row mt-2">
                <div className="col">
                  <div className="btn btn-sm btn-danger w-100" onClick={() => setScreenMode("main-screen")}>Back</div>
                </div>
              </div>
            </div>
          )}

        </>
      )}
    </div>
  );
};

export default NewOrder;
